import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { cloneDeep } from "lodash";
import { useAppSelector } from "../../../state/store";
import { homeDataAction } from "../../../state/homeState";
import { notificationDataStateActions } from "../../../state/notificationDataState";
import { validateMacroWithDFS } from "./validateMacroWithDFS";
import { CrmService } from "../../../admin/services/CrmService";
import { NodeSubType } from "../../../models/nodeSubType";
import {
  ActionTypeBackend,
  CreateOrUpdateMacroRequest,
} from "../macroNode.types";

interface UseMacroSaveOptions {
  storeId: string;
  flowId: string;
  index: number;
  stickyIndex: number;
  nodes: any[];
  edges: any[];
  setCheckError: (val: boolean) => void;
  setShowSaveModal: (val: boolean) => void;
  setSuccess: (val: boolean) => void;
}

export const useMacroSave = ({
  storeId,
  flowId,
  index,
  stickyIndex,
  nodes,
  edges,
  setCheckError,
  setShowSaveModal,
  setSuccess,
}: UseMacroSaveOptions) => {
  const dispatcher = useDispatch();
  const crmServiceRef = useRef(new CrmService());

  const stickyNotes = useAppSelector((state) => state.stickyNotesState.notes);
  const flowMeta = useAppSelector((state) => state.flowMetaState.flowMeta);
  const homeState = useAppSelector((state: any) => state.homeState);

  const showMessage = useCallback(
    (errorMessage: string, successMessage: string) => {
      dispatcher(
        notificationDataStateActions.setNotifications({
          errorMessage,
          successMessage,
        })
      );
    },
    [dispatcher]
  );

  const onFlowSaveConfirmed = useCallback(
    async (forcePublish = false, _saveType?: string, saveOnly?: boolean) => {
      !saveOnly && setCheckError(true);
      const nodesClone = cloneDeep(nodes);

      if (
        flowMeta &&
        Object.keys(flowMeta).length &&
        (flowMeta as { [key: string]: any })[flowId]
      ) {
        // Extract backend configs and inject frontendConfig locally
        const backendNodes: { [id: string]: any } = {};

        // Extract the true numerical index mapping for sticky notes and inject back into root
        const processedStickyNotes = cloneDeep(stickyNotes) as any;
        processedStickyNotes.index = stickyIndex;

        nodesClone.forEach((n: any) => {
          if (n.id.startsWith("S")) {
            // Inject geometric position directly into the sticky note payload
            if (processedStickyNotes[n.id]) {
              processedStickyNotes[n.id].position = n.position;
            }
          } else {
            const backendNodeConfig = n.data?.nodeData?.backendConfig;
            if (backendNodeConfig) {
              const descriptionNodeConfig =
                backendNodeConfig.subType === NodeSubType.MULTI_ACTION ||
                backendNodeConfig.type === ActionTypeBackend.MULTI_ACTION
                  ? { description: n.data?.nodeData?.description || [] }
                  : backendNodeConfig.subType === NodeSubType.TICKET_FORM
                    ? {
                        description: n.data?.nodeData?.description || [],
                        subType: NodeSubType.TICKET_FORM,
                      }
                    : {};

              backendNodes[n.id] = {
                ...backendNodeConfig,
                frontendConfig: {
                  position: n.position,
                  ...(Object.keys(descriptionNodeConfig).length > 0 && {
                    nodeData: descriptionNodeConfig,
                  }),
                },
              };
            }
          }
        });

        try {
          // Validation is already run by validateBeforeSave before this function is called.
          // We only need the result here to compute status + isResponseIncluded.
          const validationResult = validateMacroWithDFS(
            nodesClone.filter((n: any) => !n.id.startsWith("S")),
            edges,
            flowMeta as { [key: string]: any }
          );

          const macroMeta = (flowMeta as { [key: string]: any })[flowId] || {};
          const channels: string[] = backendNodes["N1"]?.channels ||
            macroMeta?.channels || ["whatsapp"];
          const isAgentReviewRequired: boolean =
            !!backendNodes["N1"]?.reviewRequired;

          const payload: CreateOrUpdateMacroRequest = {
            storeId,
            macroId: flowId,
            channels,
            title: macroMeta?.flowName || macroMeta?.title,
            description: macroMeta?.description,
            status: validationResult.status,
            isResponseIncluded: validationResult.isResponseIncluded,
            isAgentReviewRequired,
            index,
            config: backendNodes,
            updatedBy: homeState?.userData?.agentId || 537,
            stickyNotes: processedStickyNotes,
          };

          await crmServiceRef.current.createOrUpdateMacro(payload);

          dispatcher(
            homeDataAction.addState({
              ...homeState,
              saveFlow: false,
              lastUpdated: new Date().getTime(),
            })
          );
          setShowSaveModal(true);
          setSuccess(true);
          setCheckError(false);
        } catch (error: any) {
          showMessage(error?.message || "Error saving macro flow", "");
        }
      }
    },
    [
      storeId,
      flowId,
      index,
      stickyIndex,
      nodes,
      edges,
      stickyNotes,
      flowMeta,
      homeState,
      setCheckError,
      setShowSaveModal,
      setSuccess,
      showMessage,
      dispatcher,
    ]
  );

  return { onFlowSaveConfirmed };
};
